import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Search } from "lucide-react";
import toast from "react-hot-toast";
import {
  getDisabledModifiersSummaryAPI,
  setPresetChoiceAvailabilityAPI,
} from "../../services/api";
import { getTokenInfo } from "../../utils/auth";

interface DisabledItem {
  presetId: string;
  presetName: string;
  option: string;
  label: string;
  /** "everywhere" = off in the set itself; "location" = off only at this location */
  scope: "everywhere" | "location";
  /** When it went out of stock (ISO date) */
  since?: string | null;
  /** true = switched off before dates were tracked; since is the set's last save */
  sinceEstimated?: boolean;
}

/** "Today" / "1 day" / "12 days" since a choice went out of stock */
export const outOfStockFor = (since?: string | null) => {
  if (!since) return "";
  const days = Math.floor((Date.now() - new Date(since).getTime()) / 86400000);
  if (!Number.isFinite(days) || days < 1) return "Today";
  return days === 1 ? "1 day" : `${days} days`;
};

interface LocationSummary {
  hotelId: string;
  hotelName: string;
  count: number;
  items: DisabledItem[];
}

/**
 * Every modifier choice customers can't pick right now, per location, with a
 * one-click "Mark Available". Location admins see only their own location.
 */
const OutOfStockModifiers: React.FC = () => {
  const isMasterAdmin = getTokenInfo()?.role === "master_admin";
  const [locations, setLocations] = useState<LocationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [locationFilter, setLocationFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getDisabledModifiersSummaryAPI();
      setLocations(res.data?.data || []);
    } catch {
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return locations
      .filter((l) => locationFilter === "all" || l.hotelId === locationFilter)
      .map((l) => ({
        ...l,
        items: l.items.filter(
          (i) =>
            !q ||
            i.label.toLowerCase().includes(q) ||
            i.presetName.toLowerCase().includes(q),
        ),
      }));
  }, [locations, locationFilter, search]);
  const total = shown.reduce((sum, l) => sum + l.items.length, 0);

  const markAvailable = async (loc: LocationSummary, item: DisabledItem) => {
    const everywhere = item.scope === "everywhere";
    if (
      everywhere &&
      !window.confirm(
        `"${item.label}" is off for every location. Turn it back on everywhere?`,
      )
    ) {
      return;
    }
    const key = `${loc.hotelId}:${item.presetId}:${item.option}:${item.label}`;
    try {
      setBusyKey(key);
      await setPresetChoiceAvailabilityAPI(item.presetId, {
        option: item.option,
        label: item.label,
        available: true,
        // Turning an "everywhere" choice back on is not tied to one location
        ...(isMasterAdmin && !everywhere && { hotelId: loc.hotelId }),
      });
      toast.success(
        everywhere ? `${item.label} is available everywhere` : `${item.label} is available at ${loc.hotelName}`,
      );
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to update availability");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-4 pt-0 lg:p-8">
      <div className="sticky top-0 z-10 bg-white/90 py-6 backdrop-blur-sm">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="m-0 text-2xl font-bold text-gray-800">Out of Stock Modifiers</h1>
            <p className="m-0 mt-1 text-sm text-gray-500">
              Modifier choices customers can't pick right now ·{" "}
              <span className="font-semibold text-red-600">{loading ? "…" : total}</span> disabled
            </p>
          </div>
          <Link to="/admin/addpreset" className="text-sm text-blue-600">
            Go to Modifiers
          </Link>
        </div>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search choice or modifier set..."
              className="w-full rounded-md border py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {isMasterAdmin && (
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="h-10 rounded-md border px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Locations</option>
              {locations.map((l) => (
                <option key={l.hotelId} value={l.hotelId}>
                  {l.hotelName}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={load}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[300px] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="space-y-4">
          {shown.map((loc) => (
            <div key={loc.hotelId} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between gap-3 bg-gray-100 px-4 py-3">
                <h2 className="m-0 text-base font-bold text-gray-700">{loc.hotelName}</h2>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    loc.items.length ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                  }`}
                >
                  {loc.items.length} disabled
                </span>
              </div>
              {loc.items.length === 0 ? (
                <p className="m-0 px-4 py-3 text-sm text-gray-500">
                  {search ? "No matches." : "All modifier choices are available."}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-600">
                      <tr className="border-b">
                        <th className="p-3 text-left font-semibold">Choice</th>
                        <th className="p-3 text-left font-semibold">Modifier Set</th>
                        <th className="p-3 text-left font-semibold">Status</th>
                        <th className="p-3 text-left font-semibold">Out of Stock For</th>
                        <th className="p-3 text-right font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loc.items.map((item) => {
                        const key = `${loc.hotelId}:${item.presetId}:${item.option}:${item.label}`;
                        const everywhere = item.scope === "everywhere";
                        const canEnable = !everywhere || isMasterAdmin;
                        return (
                          <tr key={key} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="p-3 font-medium text-gray-800">{item.label}</td>
                            <td className="p-3 text-gray-600">{item.presetName}</td>
                            <td className="p-3">
                              <span
                                className={`rounded px-2 py-0.5 text-[11px] ${
                                  everywhere ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"
                                }`}
                              >
                                {everywhere ? "Off everywhere" : "Off at this location"}
                              </span>
                            </td>
                            <td className="p-3 text-gray-700">
                              {item.since ? (
                                <span title={item.sinceEstimated ? "Estimated — switched off before dates were recorded" : undefined}>
                                  <span className="font-semibold">
                                    {item.sinceEstimated ? "≈ " : ""}
                                    {outOfStockFor(item.since)}
                                  </span>
                                  <span className="block text-[11px] text-gray-500">
                                    since {new Date(item.since).toLocaleDateString()}
                                    {item.sinceEstimated ? " (estimated)" : ""}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              {canEnable ? (
                                <button
                                  onClick={() => markAvailable(loc, item)}
                                  disabled={busyKey === key}
                                  className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                                >
                                  {busyKey === key ? "Saving..." : everywhere ? "Mark Available Everywhere" : "Mark Available"}
                                </button>
                              ) : (
                                <span className="text-xs text-gray-400">Master admin only</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OutOfStockModifiers;
